<script setup>
/**
 * AgentsPerformanceChart
 * Bar chart comparing all agents' economic goal performance
 */
import { computed, ref, onMounted } from 'vue'
import { BarChart } from 'vue-chart-3'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// Register Chart.js components
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  ChartDataLabels
)

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  lastUpdated: {
    type: Date,
    default: null,
  },
})

// Chart data computed
const chartData = computed(() => {
  if (!props.data || props.data.length === 0) return null

  // Sort by analys descending
  const sortedData = [...props.data].sort((a, b) => b.analys - a.analys)

  const updateLabel = props.lastUpdated ? props.lastUpdated.toLocaleString('he-IL') : 'לא צויין'

  return {
    labels: sortedData.map((agent) => agent.name),
    datasets: [
      {
        label: `יעד כלכלי (%) - נכון ל- ${updateLabel}`,
        backgroundColor: '#409eff',
        borderColor: 'rgba(75, 192, 192, 1)',
        data: sortedData.map((agent) => agent.analys),
      },
    ],
  }
})

// Chart options
const chartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top' },
    title: { display: true, text: 'ביצועי סוכנים' },
    datalabels: {
      anchor: 'end',
      align: 'end',
      offset: -5,
      color: 'black',
      font: { weight: 'bold', size: 14 },
      textAlign: 'center',
      padding: { top: 5 },
      formatter: (value) => `${value.toFixed(0)}%`,
    },
  },
  scales: {
    x: {
      ticks: {
        autoSkip: false,
      },
      barPercentage: 0.5,
      categoryPercentage: 0.6,
    },
    y: {
      beginAtZero: true,
      ticks: {
        padding: 10,
        callback: (value) => `${parseFloat(value).toFixed(0)}%`,
      },
      suggestedMax: computed(() => {
        if (!props.data || props.data.length === 0) return 100
        return Math.max(...props.data.map((agent) => agent.analys)) * 1.2
      }).value,
    },
  },
})

// Update suggestedMax when data changes
onMounted(() => {
  if (props.data && props.data.length > 0) {
    chartOptions.value.scales.y.suggestedMax =
      Math.max(...props.data.map((agent) => agent.analys)) * 1.2
  }
})
</script>

<template>
  <div class="agents-chart-container">
    <BarChart v-if="chartData" :chart-data="chartData" :options="chartOptions" />
    <div v-else class="no-data">
      <p>אין נתונים להצגה</p>
    </div>
  </div>
</template>

<style scoped>
.agents-chart-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
}

.agents-chart-container canvas {
  width: 100% !important;
  height: auto !important;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: var(--font-size-base);
}
</style>
